// Cached, code-drawn scenery: no network assets and no per-frame tree geometry.
let cached: { width: number; height: number; seed: number; sky: HTMLCanvasElement; hills: HTMLCanvasElement; forest: HTMLCanvasElement } | undefined;

function randomFor(seed: number) {
  let value = seed >>> 0;
  return () => {
    value = (Math.imul(value, 1664525) + 1013904223) >>> 0;
    return value / 4294967296;
  };
}

function layer(width: number, height: number) {
  const canvas = document.createElement("canvas");
  canvas.width = Math.ceil(width); canvas.height = Math.ceil(height);
  return { canvas, ctx: canvas.getContext("2d")! };
}

function paint(width: number, height: number, seed: number) {
  const random = randomFor(seed);
  const extra = 160;
  const sky = layer(width, height);
  const hills = layer(width + extra * 2, height + 80);
  const forest = layer(width + extra * 2, height + 80);
  const skyGradient = sky.ctx.createLinearGradient(0, 0, 0, height);
  skyGradient.addColorStop(0, "#efe4c8"); skyGradient.addColorStop(0.48, "#e5dfbd"); skyGradient.addColorStop(1, "#c5cfaa");
  sky.ctx.fillStyle = skyGradient; sky.ctx.fillRect(0, 0, width, height);
  const sunlight = sky.ctx.createRadialGradient(width * 0.77, height * 0.1, 5, width * 0.77, height * 0.1, width * 0.58);
  sunlight.addColorStop(0, "rgba(255,244,200,.88)"); sunlight.addColorStop(0.55, "rgba(251,229,171,.22)"); sunlight.addColorStop(1, "rgba(250,237,197,0)");
  sky.ctx.fillStyle = sunlight; sky.ctx.fillRect(0, 0, width, height);
  for (const [index, color] of ["#b7c09c", "#a6b390", "#93a481"].entries()) {
    const base = height * (0.61 + index * 0.13);
    hills.ctx.fillStyle = color;
    hills.ctx.beginPath(); hills.ctx.moveTo(0, height + 80);
    for (let x = 0; x <= width + extra * 2 + 20; x += 20) {
      const y = base + Math.sin(x / (160 + index * 80) + seed % 7) * height * 0.065 + Math.sin(x / 370 + index) * height * 0.055;
      hills.ctx.lineTo(x, y);
    }
    hills.ctx.lineTo(width + extra * 2, height + 80); hills.ctx.closePath(); hills.ctx.fill();
  }

  const tree = (x: number, floor: number, size: number, palette: string[], opacity: number) => {
    const ctx = forest.ctx;
    ctx.save(); ctx.globalAlpha = opacity;
    const lean = (random() - 0.5) * size * 0.15;
    ctx.strokeStyle = "#786d4d"; ctx.lineWidth = size * 0.04; ctx.lineCap = "round";
    ctx.beginPath(); ctx.moveTo(x, floor); ctx.lineTo(x + lean, floor - size * 0.72); ctx.stroke();
    for (let branch = 0; branch < 4; branch += 1) {
      const side = branch % 2 ? 1 : -1;
      const y = floor - size * (0.4 + branch * 0.1);
      ctx.lineWidth = size * 0.02;
      ctx.beginPath(); ctx.moveTo(x + lean * 0.5, y); ctx.lineTo(x + lean + side * size * 0.2, y - size * 0.2); ctx.stroke();
    }
    for (let leaf = 0; leaf < 58; leaf += 1) {
      ctx.fillStyle = palette[Math.floor(random() * palette.length)];
      const leafX = x + lean + (random() - 0.5) * size * 0.58;
      const leafY = floor - size * (0.59 + random() * 0.36);
      ctx.beginPath(); ctx.ellipse(leafX, leafY, size * (0.045 + random() * 0.06), size * (0.04 + random() * 0.065), random(), 0, Math.PI * 2); ctx.fill();
    }
    ctx.restore();
  };
  for (let x = 0; x < width + extra * 2; x += 24 + random() * 20) {
    tree(x, height * 0.81 + Math.sin(x / 170) * 18, height * (0.16 + random() * 0.16), ["#a6b18b", "#b1b88e", "#9aaa85"], 0.8);
  }
  for (let x = 0; x < width + extra * 2; x += 45 + random() * 33) {
    tree(x, height * 0.99 + Math.sin(x / 110) * 11, height * (0.29 + random() * 0.22), ["#7e946d", "#93a379", "#a7ad79", "#b1b67c"], 0.72);
  }
  // Taller framing trees leave the centre airy, keeping the Goos readable.
  for (const x of [extra - 35, width + extra + 25]) {
    tree(x, height + 80, height * 0.9, ["#71845f", "#8c9a69", "#9faa72", "#b7ba7f"], 0.75);
  }
  const haze = forest.ctx.createLinearGradient(0, 0, 0, height);
  haze.addColorStop(0, "rgba(243,233,201,.24)"); haze.addColorStop(0.7, "rgba(239,230,197,.12)"); haze.addColorStop(1, "rgba(209,202,161,.38)");
  forest.ctx.fillStyle = haze; forest.ctx.fillRect(0, 0, width + extra * 2, height + 80);
  return { width, height, seed, sky: sky.canvas, hills: hills.canvas, forest: forest.canvas };
}

export function drawScenery(ctx: CanvasRenderingContext2D, width: number, height: number, camera: { x: number; y: number }, seed: number) {
  if (width < 1 || height < 1) return;
  if (!cached || cached.width !== width || cached.height !== height || cached.seed !== seed) cached = paint(width, height, seed);
  const driftX = Math.tanh(camera.x / 65) * 65;
  const driftY = Math.tanh(camera.y / 90) * 25;
  ctx.drawImage(cached.sky, 0, 0);
  ctx.drawImage(cached.hills, -160 - driftX * 0.35, -20 - driftY * 0.35);
  ctx.drawImage(cached.forest, -160 - driftX, -20 - driftY);
  // Golden shafts and a quiet central clearing echo the supplied reference.
  const clearing = ctx.createRadialGradient(width * 0.51, height * 0.4, 0, width * 0.51, height * 0.4, width * 0.45);
  clearing.addColorStop(0, "rgba(249,237,203,.26)"); clearing.addColorStop(1, "rgba(249,237,203,0)");
  ctx.fillStyle = clearing; ctx.fillRect(0, 0, width, height);
}
